// Seasonal campaign band (API_CONTRACT §2i, G68): GET /campaigns/current
// returns the one live campaign or null. Colours come from the server's
// fixed AA-checked palette, text only from the admin. Anything malformed
// means "no band" (never a half-drawn one).

import { API_URL, getJson } from './api';
import { bannerHref } from './banners';
import { FACTS, readFacts, writeFacts } from './factsCache';

const HEX = /^#[0-9A-Fa-f]{6}$/;
export const ORNAMENTS = ['crescent', 'lanterns', 'flag_ribbon', 'confetti', 'stars', 'none'];

const text = (value) => ({
  en: typeof value?.en === 'string' ? value.en.trim() : '',
  ar: typeof value?.ar === 'string' ? value.ar.trim() : '',
});

/**
 * In-app path only ("/collections/the-edit"); anything else → no button. The
 * admin validates links with the banner rules (`/product/<id>`,
 * `/shop?cat=<slug>`), so map them to real storefront routes the same way.
 */
const safeLink = (link) => (
  typeof link === 'string' && /^\/(?!\/)[^\s\\]*$/.test(link) ? bannerHref(link) || '' : ''
);

export const normalizeCampaign = (data) => {
  if (!data || typeof data !== 'object' || !data.id) return null;
  const palette = data.palette || {};
  if (![palette.background, palette.foreground, palette.ornament].every((c) => HEX.test(c || ''))) return null;
  const headline = text(data.headline);
  if (!headline.en) return null;
  return {
    id: String(data.id),
    key: String(data.key || 'custom'),
    palette: { background: palette.background, foreground: palette.foreground, ornament: palette.ornament },
    ornament: ORNAMENTS.includes(data.ornament) ? data.ornament : 'none',
    headline,
    subline: text(data.subline),
    ctaLabel: text(data.cta_label),
    ctaLink: safeLink(data.cta_link),
  };
};

/** The live campaign, or null (none, or the call failed). */
export const fetchCurrentCampaign = async () => {
  try {
    const data = await getJson(`${API_URL}/campaigns/current`, { timeoutMs: 10000 });
    writeFacts(FACTS.campaign, data && typeof data === 'object' ? data : null);
    return normalizeCampaign(data);
  } catch {
    return null;
  }
};

const inWindow = (data, now) => {
  const starts = Date.parse(data.starts_at || '');
  const ends = Date.parse(data.ends_at || '');
  return !(Number.isFinite(starts) && starts > now) && !(Number.isFinite(ends) && ends <= now);
};

/**
 * The last answer (≤ 10 min, utils/factsCache) for the first paint, while
 * fetchCurrentCampaign asks again: `undefined` when nothing is remembered,
 * else the campaign or null. A remembered campaign whose window has closed
 * is never shown.
 */
export const cachedCampaign = (now = Date.now()) => {
  const data = readFacts(FACTS.campaign);
  if (data === undefined) return undefined;
  if (!data || typeof data !== 'object') return null;
  return inWindow(data, now) ? normalizeCampaign(data) : null;
};
